import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { usersAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SEO from "../components/SEO.jsx";

const G = {
  bg: "#0A0A0A", surface: "#111111", border: "#1E1E1E",
  text: "#E4E4DC", muted: "#666", accent: "#00C896",
  purple: "#7C6FFF", danger: "#FF6B6B",
  font: "'Sora', sans-serif", mono: "'DM Mono', monospace",
};

const Avatar = ({ initials, size = 64, color = G.accent }) => (
  <div style={{ width:size, height:size, borderRadius:"50%", background:"#1A1A1A", border:`2px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.3, fontWeight:700, color, fontFamily:G.mono, flexShrink:0 }}>
    {initials?.slice(0,2).toUpperCase()}
  </div>
);

const StatBox = ({ label, value, color = G.accent }) => (
  <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:"14px 20px", textAlign:"center" }}>
    <div style={{ fontSize:22, fontWeight:700, color, fontFamily:G.mono }}>{value}</div>
    <div style={{ fontSize:11, color:G.muted, marginTop:4 }}>{label}</div>
  </div>
);

export default function ProfilePage() {
  const { username } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab]           = useState("questions");
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    setProfile(null);
    usersAPI.get(username)
      .then(res => setProfile(res.data))
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [username]);

  const handleFollow = async () => {
    if (!me) return navigate("/login");
    try {
      const res = await usersAPI.follow(profile.user.id);
      setFollowing(res.data.following);
      setProfile(prev => ({
        ...prev,
        user: {
          ...prev.user,
          follower_count: res.data.following
            ? prev.user.follower_count + 1
            : prev.user.follower_count - 1,
        }
      }));
    } catch {}
  };

  if (loading) return <div style={{ textAlign:"center", padding:"80px 0", color:G.muted, fontFamily:G.font }}>Loading...</div>;
  if (notFound) return (
    <div style={{ textAlign:"center", padding:"80px 16px", fontFamily:G.font }}>
      <SEO title="User Not Found" noindex />
      <div style={{ fontSize:64, marginBottom:16 }}>404</div>
      <h1 style={{ fontSize:24, fontWeight:700, color:G.text, marginBottom:12 }}>User Not Found</h1>
      <p style={{ color:G.muted, marginBottom:24 }}>The user @{username} doesn't exist.</p>
      <Link to="/feed" style={{ color:G.accent, textDecoration:"none", fontFamily:G.mono }}>← Back to Feed</Link>
    </div>
  );
  if (loadError || !profile) return (
    <div style={{ textAlign:"center", padding:"80px 16px", fontFamily:G.font }}>
      <SEO title="Profile" noindex />
      <p style={{ color:G.danger, marginBottom:24 }}>Something went wrong loading this profile. Please try again.</p>
      <Link to="/feed" style={{ color:G.accent, textDecoration:"none", fontFamily:G.mono }}>← Back to Feed</Link>
    </div>
  );

  const { user, questions, answers } = profile;
  const joinDate = new Date(user.created_at).toLocaleDateString("en-US", { month:"long", year:"numeric" });
  const profileDesc = user.bio
    ? `${user.bio.slice(0, 140)} — ${user.reputation} reputation on askFOGRA.`
    : `${user.username} has ${questions.length} questions and ${answers.length} answers on askFOGRA. Joined ${joinDate}.`;

  return (
    <div style={{ maxWidth:780, margin:"0 auto", padding:"32px 16px", fontFamily:G.font }}>
      <SEO title={`${user.username} — Profile`} description={profileDesc} url={`/u/${user.username}`} />

      {/* Header */}
      <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:16, padding:28, marginBottom:20 }}>
        <div className="profile-header" style={{ display:"flex", alignItems:"flex-start", gap:20, flexWrap:"wrap" }}>
          <Avatar initials={user.username} size={72} />
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", marginBottom:6 }}>
              <h1 style={{ fontSize:22, fontWeight:700, color:G.text, margin:0 }}>{user.username}</h1>
              {me && me.username !== user.username && (
                <button onClick={handleFollow} style={{
                  background: following ? "#1A1A1A" : G.accent,
                  color: following ? G.muted : "#000",
                  border: `1px solid ${following ? "#333" : G.accent}`,
                  padding:"5px 16px", borderRadius:20, cursor:"pointer",
                  fontSize:12, fontFamily:G.mono, fontWeight:600,
                  transition:"all 0.15s"
                }}>
                  {following ? "Following" : "+ Follow"}
                </button>
              )}
            </div>
            {user.bio && <p style={{ color:G.muted, fontSize:14, lineHeight:1.6, marginBottom:10 }}>{user.bio}</p>}
            <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
              <span style={{ fontSize:13, color:"#555", fontFamily:G.mono }}>
                <span style={{ color:G.text, fontWeight:600 }}>{user.follower_count}</span> followers
              </span>
              <span style={{ fontSize:13, color:"#555", fontFamily:G.mono }}>
                <span style={{ color:G.text, fontWeight:600 }}>{user.following_count}</span> following
              </span>
              <span style={{ fontSize:12, color:"#3A3A3A", fontFamily:G.mono }}>📅 Joined {joinDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="profile-stats" style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12, marginBottom:24 }}>
        <StatBox label="Questions" value={questions.length} color={G.accent} />
        <StatBox label="Answers" value={answers.length} color={G.purple} />
        <StatBox label="Upvotes Received" value={user.total_upvotes} color="#FFB347" />
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:16, background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:4, width:"fit-content" }}>
        {["questions","answers"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab===t ? "#1A1A1A" : "transparent",
            border: tab===t ? `1px solid #2A2A2A` : "1px solid transparent",
            color: tab===t ? G.accent : G.muted,
            padding:"6px 18px", borderRadius:7, cursor:"pointer",
            fontSize:13, fontFamily:G.mono, textTransform:"capitalize",
            transition:"all 0.15s"
          }}>{t}</button>
        ))}
      </div>

      {/* Questions Tab */}
      {tab === "questions" && (
        <div>
          {questions.length === 0
            ? <div style={{ textAlign:"center", padding:"40px 0", color:"#333" }}>No questions yet</div>
            : questions.map(q => (
              <Link key={q.id} to={`/q/${q.slug}`} style={{ textDecoration:"none" }}>
                <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:"14px 18px", marginBottom:8, transition:"border-color 0.15s" }}
                  onMouseOver={e => e.currentTarget.style.borderColor="#2A2A2A"}
                  onMouseOut={e => e.currentTarget.style.borderColor=G.border}>
                  <div style={{ fontSize:14, fontWeight:600, color:G.text, marginBottom:6, lineHeight:1.4 }}>{q.title}</div>
                  <div style={{ display:"flex", gap:14 }}>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>▲ {q.vote_score}</span>
                    <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))
          }
        </div>
      )}

      {/* Answers Tab */}
      {tab === "answers" && (
        <div>
          {answers.length === 0
            ? <div style={{ textAlign:"center", padding:"40px 0", color:"#333" }}>No answers yet</div>
            : answers.map(a => (
              <Link key={a.id} to={`/q/${a.question_slug}`} style={{ textDecoration:"none" }}>
                <div style={{ background:G.surface, border:`1px solid ${a.is_accepted?G.accent:G.border}`, borderRadius:10, padding:"14px 18px", marginBottom:8, position:"relative", transition:"border-color 0.15s" }}
                  onMouseOver={e => e.currentTarget.style.borderColor=a.is_accepted?G.accent:"#2A2A2A"}
                  onMouseOut={e => e.currentTarget.style.borderColor=a.is_accepted?G.accent:G.border}>
                  {a.is_accepted && (
                    <div style={{ position:"absolute", top:-1, right:14, background:G.accent, color:"#000", fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:"0 0 6px 6px", fontFamily:G.mono }}>✓ ACCEPTED</div>
                  )}
                  <div style={{ fontSize:12, color:G.muted, marginBottom:6, fontFamily:G.mono }}>
                    Re: <span style={{ color:G.purple }}>{a.question_title}</span>
                  </div>
                  <div style={{ fontSize:14, color:"#CCC", lineHeight:1.5, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{a.body}</div>
                  <div style={{ display:"flex", gap:14, marginTop:8 }}>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>▲ {a.vote_score}</span>
                    <span style={{ fontSize:11, color:"#3A3A3A", fontFamily:G.mono }}>{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))
          }
        </div>
      )}
    </div>
  );
}
